import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useProfile } from '../context/ProfileContext';
import { useSession } from '../context/SessionContext';
import { createProfile, archiveProfile } from '../services/profilesService';
import InitialAvatar from './InitialAvatar';
import IconButton from './IconButton';
import './comic.css';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Fixed name for the throwaway second Hero created ONLY when the account
// has just one -- deliberately reads as a placeholder/demo entity rather
// than a plausible real teammate name a user might want to keep, since it's
// archived again (see cleanupDemo below) the instant the tour either
// finishes or is dismissed early.
const DEMO_HERO_NAME = "Hero B";
const DEMO_ROUTINE_NAME = "Tour Demo";

// A synthetic, never-persisted "routine" session -- RoutinePicker.jsx
// renders a live ActiveMission purely off SessionContext's sessionsByProfile
// map, with no requirement that a matching routine doc exist in Firestore,
// so the tour can demo the full live-Mission UI (exercise cards, history/
// note icons, Cancel Mission) without ever writing a routines/{id} doc at
// all -- there's nothing to clean up afterward on that front, only the
// session state itself (ended via the same real Cancel Mission button the
// tour clicks) and, if one was created, the temporary second Hero.
const buildDemoSession = (exercise) => ({
  routineId: null,
  routineName: DEMO_ROUTINE_NAME,
  exercises: [
    {
      exerciseId: exercise.id,
      order: 0,
      sets: 1,
      name: exercise.name,
      targetMuscle: exercise.targetMuscle,
      mainMuscle: exercise.mainMuscle
    }
  ],
  startTime: new Date().toISOString(),
  status: "active"
});

// Finds the currently VISIBLE match for a selector, not just the first one
// in document order -- necessary wherever a target can legitimately exist
// twice at once (e.g. the exercise history/note icons, duplicated across
// Hero A's and Hero B's ActiveMission instances once both have a live demo
// Mission, each hidden via display:none unless it's the focused profile's --
// see RoutinePicker.jsx's own "stay mounted, hide via CSS" pattern). A
// plain querySelector would silently grab whichever instance happens to
// render first in the DOM regardless of which one is actually on screen.
const queryVisible = (selector) => {
  const matches = document.querySelectorAll(selector);
  for (const el of matches) {
    if (el.offsetParent !== null) return el;
  }
  return null;
};

const clickVisible = async (selector, waitAfterMs = 350) => {
  const el = queryVisible(selector);
  if (el) el.click();
  await sleep(waitAfterMs);
  return el;
};

// Polls a live ref (not a value captured in a closure -- see sessionsRef in
// the component below) until the given profile's session is gone, i.e.
// until the REAL user has actually tapped the real Discard button
// themselves. onEnter is a plain async function called once per step entry,
// so it has no reactive way to "wait for a future state update" other than
// polling something that's kept genuinely live. A generous two-minute cap
// is a pure safety net (never expected to trigger in normal use) so this
// can't hang the tour forever if something goes wrong -- cleanupDemo still
// catches a lingering demo Mission if the tour is dismissed while waiting.
const waitForSessionEnd = async (sessionsRef, profileId, timeoutMs = 120000) => {
  const start = Date.now();
  while (sessionsRef.current[profileId] && Date.now() - start < timeoutMs) {
    await sleep(200);
  }
};

/**
 * Step list. Each step is either a plain look-at-this step (`tab`/
 * `selector`/`title`/`text`) or additionally carries an `onEnter(api)`
 * async action, run once whenever the tour transitions INTO that step
 * (forward OR backward -- re-running an already-run action again is
 * harmless/idempotent for everything this tour does, so no extra state is
 * needed to guard against a repeat). `api` is assembled fresh inside the
 * component on every call (see runOnEnter below) rather than captured here,
 * since these objects are defined once at module scope and can't close over
 * component state/hooks directly.
 *
 * The 9-step contiguous block from "start-mission" through "all-clean" is
 * the interactive live-Mission demo added per direct user request to "go a
 * bit deeper" -- it's spliced in conditionally (see the `active` effect
 * below) only when the account has no real session already in progress
 * anywhere, so the tour can never clobber an actual in-progress workout.
 */
const BASE_STEPS = [
  {
    tab: null,
    selector: null,
    title: "Welcome to Pumpous!",
    text: "Let's take a deeper tour so you know your way around before your first real Mission."
  },
  {
    tab: null,
    selector: '[data-tour="hero-switcher"]',
    title: "Your Hero Roster",
    text: "Switch between Heroes here, or add a new one -- each Hero tracks their own Missions and progress independently."
  },
  {
    tab: "routines",
    selector: '[data-tour="tab-routines"]',
    title: "Select Mission",
    text: "Home base. Build routines here, then start a Mission whenever you're ready to train.",
    centerCard: true
  }
];

const LIVE_DEMO_STEPS = [
  {
    tab: "routines",
    selector: '[data-tour="mission-header"]',
    title: "Starting a Mission",
    text: "Let's start a quick demo Mission so I can show you what's inside -- don't worry, we'll cancel it before we're done, nothing gets saved.",
    onEnter: async ({ ctx, startSession, activeProfile }) => {
      if (ctx.skipLiveDemo) return;
      const exercise = await ctx.ensureDemoExercise();
      if (!exercise || !activeProfile) return;
      ctx.heroAId = activeProfile.id;
      startSession(ctx.heroAId, buildDemoSession(exercise));
      await sleep(300);
    }
  },
  {
    tab: "routines",
    selector: '[data-tour="exercise-history-btn"]',
    title: "Exercise History",
    text: "This icon shows every past set logged for THIS exercise specifically -- a focused view, separate from your full workout history."
  },
  {
    tab: "routines",
    selector: '[data-tour="exercise-note-btn"]',
    title: "Exercise Notes",
    text: "Leave yourself a note on any exercise, any day -- form cues, how it felt, anything worth remembering next time."
  },
  {
    tab: "routines",
    selector: '[data-tour="active-sessions-bar"]',
    title: "Two Workouts at Once",
    text: "Pumpous supports more than one Hero training at the same time. We just started a second Mission for a practice Hero -- both are live right now.",
    onEnter: async ({ ctx, startSession, profiles, activeProfile, refreshProfiles, userId }) => {
      if (ctx.skipLiveDemo) return;
      if (!ctx.heroBId) {
        const existing = profiles.find((p) => p.isActive !== false && p.id !== activeProfile?.id);
        if (existing) {
          ctx.heroBId = existing.id;
        } else {
          const result = await createProfile(userId, DEMO_HERO_NAME);
          if (result.success) {
            ctx.heroBId = result.id;
            ctx.heroBIsTemp = true;
            await refreshProfiles();
            await sleep(300);
          }
        }
      }
      if (!ctx.heroBId) return;
      const exercise = await ctx.ensureDemoExercise();
      if (!exercise) return;
      startSession(ctx.heroBId, buildDemoSession(exercise));
      await sleep(300);
    }
  },
  {
    tab: "routines",
    selector: '[data-tour="active-sessions-bar"]',
    title: "Switching Between Heroes",
    text: "Tap any Hero's pill here to jump straight to their Mission. We just switched over -- notice the avatar above changed too.",
    onEnter: async ({ ctx }) => {
      if (ctx.skipLiveDemo || !ctx.heroBId) return;
      await clickVisible(`[data-tour-profile="${ctx.heroBId}"]`);
    }
  },
  {
    tab: "routines",
    // Highlights the real "Cancel Mission?" confirm dialog itself (see
    // Modal.jsx's .comic-modal class), per direct user feedback -- it used
    // to have no selector at all, which left the dialog fully dimmed/washed
    // out along with everything else instead of being the thing actually
    // called out.
    selector: '.comic-modal',
    title: "Cancelling a Mission",
    // Per direct user request: this is no longer narration for an action
    // the tour performs automatically -- the real user has to genuinely
    // tap Discard themselves (Next stays disabled, and the real "Keep
    // Going" button is locked out too, until they do -- see onEnter below
    // and ActiveMission.jsx's lockCancelConfirm prop), and the tour then
    // advances itself the instant that happens -- no need to also tap Next.
    text: "Tap Discard to cancel this Hero's demo Mission for real -- nothing gets saved. We'll move on as soon as you do.",
    // Once this step's real action has genuinely happened, it's "consumed"
    // -- there's no real dialog left to reopen for a real-but-now-gone
    // Mission, so both Back and Next skip straight over it on any later
    // pass (see handleBack/handleNext) rather than landing on a dead card
    // showing stale instructions with nothing behind them to interact
    // with -- a real bug reported via screenshot after the onEnter guard
    // above was added (that guard fixed the stray-dialog bug, but left
    // this step itself unreachable-but-still-landable as a dead end).
    isConsumed: (ctx) => ctx.discardedB,
    onEnter: async ({ ctx, sessionsRef, onLockProfile, setWaitingForUser, goToNextStep }) => {
      if (ctx.skipLiveDemo || !ctx.heroBId) return;
      // Guards against re-running this for real on a revisit (navigating
      // Back into this step after it already completed, then forward again,
      // or Back past it from a later step) -- without this, onEnter would
      // blindly click WHATEVER real "End Mission" button happens to be
      // visible at that moment, which by then could belong to a DIFFERENT
      // Hero's real, unrelated Mission (e.g. the user's own, after focus
      // already switched back in the next step) -- a real bug reported via
      // screenshot: a stray, wrongly-targeted Cancel Mission dialog was left
      // open with nothing to close it. Once the real discard has genuinely
      // happened once, this step is inert on every later visit.
      if (ctx.discardedB) return;
      await clickVisible('[data-tour="mission-actions"] .comic-btn--end');
      // Locks the real "Keep Going" button (and blocks the dialog's X/
      // backdrop/Escape dismissal) for exactly this one Hero's confirm
      // dialog, so there's no way to back out of a demo Mission the tour
      // promised to clean up -- see ActiveMission.jsx. Cleared the instant
      // the real Discard click lands (detected below) or, as a safety net,
      // if the tour itself is dismissed mid-wait (cleanupDemo).
      onLockProfile(ctx.heroBId);
      // setWaitingForUser(true) keeps this step's own instructional text on
      // screen (instead of the generic "One moment..." the card otherwise
      // shows during an onEnter action) for however long it takes the real
      // user to actually read it and tap Discard -- see its declaration.
      setWaitingForUser(true);
      await waitForSessionEnd(sessionsRef, ctx.heroBId);
      onLockProfile(null);
      ctx.discardedB = true;
      // Auto-advance -- the real action this step was waiting on just
      // happened, so there's nothing left for Next to add; per direct user
      // request, the tour moves on by itself rather than making the user
      // tap Next right after they already tapped the real Discard button.
      goToNextStep();
    }
  },
  {
    tab: "routines",
    selector: '[data-tour="active-sessions-bar"]',
    title: "Back to You",
    text: "That demo Mission is cancelled -- gone for good, nothing saved. Switching back to your own Hero now.",
    onEnter: async ({ ctx }) => {
      if (ctx.skipLiveDemo) return;
      // Discard already happened for real in the previous step -- nothing
      // left to click here except switching focus back.
      if (ctx.heroAId) await clickVisible(`[data-tour-profile="${ctx.heroAId}"]`);
    }
  },
  {
    tab: "routines",
    selector: '.comic-modal', // see the "Cancelling a Mission" step's comment above
    title: "Cleaning Up",
    text: "Let's cancel this last demo Mission the same way -- tap Discard, and we'll continue automatically.",
    isConsumed: (ctx) => ctx.discardedA, // see the "Cancelling a Mission" step's comment above
    onEnter: async ({ ctx, sessionsRef, onLockProfile, setWaitingForUser, goToNextStep }) => {
      if (ctx.skipLiveDemo) return;
      // Same revisit guard as "Cancelling a Mission" above, and for the
      // same reason -- prevents a Back/Forward replay from clicking
      // whatever real Mission-ending button happens to be on screen.
      if (ctx.discardedA) return;
      await clickVisible('[data-tour="mission-actions"] .comic-btn--end');
      onLockProfile(ctx.heroAId);
      setWaitingForUser(true);
      await waitForSessionEnd(sessionsRef, ctx.heroAId);
      onLockProfile(null);
      ctx.discardedA = true;
      goToNextStep();
    }
  },
  {
    tab: "routines",
    selector: null,
    title: "All Clear",
    text: "Both demo Missions are cancelled and nothing was saved -- your dashboard's exactly as it was before we started.",
    onEnter: async ({ ctx, refreshProfiles }) => {
      if (ctx.skipLiveDemo) return;
      // Discard already happened for real in the previous step -- just the
      // temporary second Hero cleanup is left. Guarded so revisiting this
      // step (Back then Forward again) doesn't re-archive an already-
      // archived profile a second time.
      if (ctx.heroBIsTemp && ctx.heroBId && !ctx.heroBArchived) {
        await archiveProfile(ctx.heroBId);
        ctx.heroBArchived = true;
        await refreshProfiles();
      }
      await sleep(200);
    }
  }
];

const REMAINING_STEPS = [
  {
    tab: "quickstart",
    selector: '[data-tour="tab-quickstart"]',
    title: "Quick Start",
    text: "Log a workout on the fly, no routine required -- perfect for an improvised session.",
    centerCard: true
  },
  {
    tab: "exercises",
    selector: '[data-tour="tab-exercises"]',
    title: "Manage My Exercises",
    text: "Create your own custom exercises here, or merge duplicates into the official library.",
    centerCard: true
  },
  {
    tab: "history",
    selector: '[data-tour="tab-history"]',
    title: "Previously...",
    text: "Your full training history -- every set you've ever logged, grouped by day.",
    centerCard: true
  },
  {
    tab: "shop",
    selector: '[data-tour="tab-shop"]',
    title: "Shop",
    text: "Real Pumpous gear, ready to order.",
    centerCard: true
  },
  {
    tab: null,
    selector: '[data-tour="contact-button"]',
    title: "Need a Hand?",
    text: "Tap Contact any time to send feedback or ask for help -- it goes straight to our team."
  },
  {
    tab: null,
    selector: null,
    title: "You're Ready, Hero!",
    text: "That's the tour. Head to Select Mission to build your first routine and start training."
  }
];

const MARGIN = 16;
const CARD_WIDTH = 320;

// Two stacked triangles (dark outline, then a white fill inset ~3-4px) --
// the same "bordered CSS triangle" technique .comic-speech-bubble's own
// fixed left-pointing tail already uses (see comic.css), generalized here
// to real DOM divs (not CSS pseudo-elements, which can't take a
// JS-computed position) so the tail can dynamically point up or down and
// land at whatever horizontal offset lines up with the real highlighted
// target -- GuidedTour's card always stacks directly above or below its
// target (never beside it), so only these two directions are ever needed.
// Both triangles share the same "flush" edge (touching the bubble itself)
// and differ only in how far their tip extends and how wide they are,
// exactly mirroring the outer/inner proportions .comic-speech-bubble's own
// tail uses (13/11 outer, 9/8 inner).
const buildTailStyles = (direction, offsetPx) => {
  const base = {
    position: "absolute",
    left: offsetPx,
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderStyle: "solid"
  };
  // The colored (base) side's border-width is 2px deeper than the tail's
  // own visual depth (13/9px) -- the `top`/`bottom` offset below is what
  // positions the TIP, so it's left unchanged; the extra depth just lets
  // the flat base overlap 2px INTO the card past its own border instead of
  // landing exactly flush with it. Plain CSS border math places the base
  // exactly at the padding-box edge with nothing guaranteeing sub-pixel
  // overlap with the card's own separately-rendered border -- reported
  // directly by the user via screenshot as a thin line visible across
  // several of this app's tail arrows (same fix applied to
  // .comic-speech-bubble's and .comic-alert-bubble's tails in comic.css).
  if (direction === "up") {
    return {
      outer: { ...base, top: "-13px", borderWidth: "0 11px 15px 11px", borderColor: "transparent transparent #000000 transparent" },
      inner: { ...base, top: "-9px", borderWidth: "0 8px 11px 8px", borderColor: "transparent transparent #ffffff transparent" }
    };
  }
  return {
    outer: { ...base, bottom: "-13px", borderWidth: "15px 11px 0 11px", borderColor: "#000000 transparent transparent transparent" },
    inner: { ...base, bottom: "-9px", borderWidth: "11px 8px 0 8px", borderColor: "#ffffff transparent transparent transparent" }
  };
};

/**
 * Guided onboarding tour -- see BASE_STEPS/LIVE_DEMO_STEPS/REMAINING_STEPS
 * above for the full step list and rationale. Two already-proven patterns
 * from elsewhere in this codebase power the overlay itself: Modal.jsx's
 * createPortal-to-document.body (immune to any ancestor's clip-path/
 * transform breaking position:fixed) and ExercisePicker.jsx's measure-then-
 * retry positioning (scrollIntoView, a few retry timers, scroll/resize
 * listeners).
 */
const GuidedTour = ({ active, activeTab, onTabChange, onFinish, userId, onLockProfile }) => {
  const { activeProfile, profiles, selectProfile, refreshProfiles } = useProfile();
  const { startSession, endSession, sessionsByProfile } = useSession();

  const [steps, setSteps] = useState(BASE_STEPS.concat(REMAINING_STEPS));
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  // Set by the "Cancelling a Mission"/"Cleaning Up" steps' onEnter right
  // before they start waiting on a real Discard tap (see waitForSessionEnd
  // below), cleared the instant it lands. actionPending alone already
  // covers "Next/Back disabled" correctly for this whole wait -- but it was
  // ALSO driving the bubble's "One moment..." placeholder text, which made
  // sense for the brief (sub-second) setup actions elsewhere in the tour,
  // and was wrong here: this wait can last as long as the user takes to
  // read the card and tap the real button, so showing "One moment..." the
  // entire time hid the actual instruction telling them what to do. This
  // flag lets the bubble keep showing the real step text throughout the
  // wait while Next/Back stay disabled via actionPending as before.
  const [waitingForUser, setWaitingForUser] = useState(false);

  // Mirrors sessionsByProfile into a ref kept current every render, so
  // waitForSessionEnd (called from inside an onEnter async function, which
  // only ever sees a snapshot of whatever was passed in at call time) can
  // poll the REAL, live value instead of a stale closed-over one.
  const sessionsRef = useRef(sessionsByProfile);
  useEffect(() => {
    sessionsRef.current = sessionsByProfile;
  }, [sessionsByProfile]);

  // Mutable, cross-step scratch state -- which profile is "Hero A" (the
  // account's own, original Hero), which is "Hero B" (the practice Hero,
  // reused if one already exists, created+archived if not), whether that
  // creation happened (so cleanup knows whether to archive it again), the
  // one official exercise fetched once and reused for both demo Missions,
  // and whether a real session already existed anywhere on the account when
  // the tour started (if so, the whole live-Mission block is skipped
  // entirely -- never disturb an actual in-progress workout).
  const ctxRef = useRef({
    heroAId: null,
    heroBId: null,
    heroBIsTemp: false,
    heroBArchived: false,
    discardedA: false,
    discardedB: false,
    demoExercise: null,
    skipLiveDemo: false,
    ensureDemoExercise: null
  });

  // Fetches one official exercise (cached after the first call) to power
  // both demo Missions -- never a custom exercise, since the official
  // library always exists regardless of which account this is, while a
  // brand-new account has zero custom exercises of its own yet.
  const ensureDemoExercise = useCallback(async () => {
    if (ctxRef.current.demoExercise) return ctxRef.current.demoExercise;
    const snap = await getDocs(query(collection(db, "exercises"), where("userId", "==", "global"), limit(1)));
    if (snap.empty) return null;
    const d = snap.docs[0];
    const exercise = { id: d.id, ...d.data() };
    ctxRef.current.demoExercise = exercise;
    return exercise;
  }, []);
  ctxRef.current.ensureDemoExercise = ensureDemoExercise;

  // Builds the step list fresh each time the tour (re)starts -- the
  // live-Mission block is included only when nothing is already in
  // progress anywhere on the account, checked once right here rather than
  // per-step, so a real workout the user already had going is never
  // overwritten by the demo.
  useEffect(() => {
    if (!active) return;
    const hadRealSession = Object.keys(sessionsByProfile).length > 0;
    ctxRef.current = {
      heroAId: null,
      heroBId: null,
      heroBIsTemp: false,
      heroBArchived: false,
      discardedA: false,
      discardedB: false,
      demoExercise: ctxRef.current.demoExercise,
      skipLiveDemo: hadRealSession,
      ensureDemoExercise
    };
    setSteps(hadRealSession ? BASE_STEPS.concat(REMAINING_STEPS) : BASE_STEPS.concat(LIVE_DEMO_STEPS, REMAINING_STEPS));
    setStepIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const step = steps[stepIndex] || steps[0];
  const tabReady = !step.tab || step.tab === activeTab;

  useEffect(() => {
    if (!active || tabReady) return;
    onTabChange(step.tab);
  }, [active, tabReady, step, onTabChange]);

  // Runs the current step's onEnter (if any) once tabReady -- gates
  // measuring position until any real action it performs has settled.
  useEffect(() => {
    if (!active || !tabReady || !step.onEnter) return;
    let cancelled = false;
    setActionPending(true);
    setWaitingForUser(false);
    step.onEnter({
      ctx: ctxRef.current,
      startSession,
      endSession,
      selectProfile,
      profiles,
      activeProfile,
      refreshProfiles,
      userId,
      sessionsRef,
      onLockProfile,
      setWaitingForUser,
      // Lets a step auto-advance itself once some real-world condition it's
      // been waiting on is satisfied (see the two "Discard" steps below) --
      // per direct user request, the user shouldn't have to also tap Next
      // after already taking the real action the tour was waiting for.
      goToNextStep: () => setStepIndex((i) => i + 1)
    })
      .catch((err) => console.error("Tour step action failed:", err))
      .finally(() => {
        if (!cancelled) setActionPending(false);
        setWaitingForUser(false);
      });
    return () => {
      cancelled = true;
    };
    // Re-running on every stepIndex change into a step with onEnter is
    // intentional (see the step-list comment above) -- only stepIndex itself
    // should retrigger this, not every identity change of the callbacks
    // below (all stable enough in practice; re-running on their account
    // would re-fire on unrelated re-renders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tabReady, stepIndex]);

  const updatePosition = useCallback(() => {
    if (!step.selector) {
      setRect(null);
      return;
    }
    const el = queryVisible(step.selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    // Normally gated on !actionPending (don't measure mid-setup). The two
    // "Discard" steps are the one exception: their onEnter stays pending
    // for as long as the real user takes to tap the real button (see
    // waitForSessionEnd), and the thing that needs highlighting -- the real
    // confirm dialog they just opened -- only exists once that wait has
    // actually started. waitingForUser marks exactly that window, so
    // positioning can run during it despite actionPending still being true.
    if (!active || !tabReady || (actionPending && !waitingForUser)) return undefined;
    const el = step.selector && queryVisible(step.selector);
    if (el) el.scrollIntoView({ block: "center" });
    updatePosition();
    const retryTimers = [50, 150, 300].map((delay) => setTimeout(updatePosition, delay));
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      retryTimers.forEach(clearTimeout);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [active, tabReady, actionPending, waitingForUser, step, updatePosition]);

  // Safety net: if the tour is dismissed (Skip, Escape, backdrop click) or
  // reaches the end while a demo Mission is still technically live (e.g.
  // dismissed mid-demo, before the tour's own cancel steps ran), end it
  // directly via SessionContext rather than leaving a "Tour Demo" Mission
  // stranded on the dashboard -- and archive the temporary second Hero, if
  // one was created. Also clears any lockCancelConfirm lock still held on a
  // Hero's real Cancel-Mission dialog, in case the tour was dismissed while
  // "Cancelling a Mission"/"Cleaning Up" was mid-wait for a real Discard tap
  // -- otherwise that Hero's "Keep Going" button would stay disabled
  // forever with no tour left to ever clear it. A no-op in the common case
  // where the tour's own cancel-mission steps already handled all of this.
  const cleanupDemo = useCallback(async () => {
    const ctx = ctxRef.current;
    if (ctx.heroAId && sessionsByProfile[ctx.heroAId]?.routineName === DEMO_ROUTINE_NAME) {
      endSession(ctx.heroAId);
    }
    if (ctx.heroBId && sessionsByProfile[ctx.heroBId]?.routineName === DEMO_ROUTINE_NAME) {
      endSession(ctx.heroBId);
    }
    onLockProfile(null);
    if (ctx.heroBIsTemp && ctx.heroBId) {
      await archiveProfile(ctx.heroBId);
      refreshProfiles();
    }
  }, [sessionsByProfile, endSession, refreshProfiles, onLockProfile]);

  const handleFinish = useCallback(() => {
    cleanupDemo();
    onFinish();
  }, [cleanupDemo, onFinish]);

  useEffect(() => {
    if (!active) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleFinish();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [active, handleFinish]);

  if (!active) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  // A step with isConsumed(ctx) true has already genuinely happened for
  // real and has nothing left to show or do -- skip over it in whichever
  // direction navigation is moving, rather than landing on a dead card
  // (see the "Cancelling a Mission"/"Cleaning Up" steps' isConsumed).
  const isStepConsumed = (idx) => {
    const s = steps[idx];
    return !!(s && s.isConsumed && s.isConsumed(ctxRef.current));
  };

  const handleNext = () => {
    if (actionPending) return;
    if (isLast) {
      handleFinish();
      return;
    }
    setStepIndex((i) => {
      let next = i + 1;
      while (next < steps.length - 1 && isStepConsumed(next)) next += 1;
      return next;
    });
  };
  const handleBack = () => {
    if (actionPending) return;
    setStepIndex((i) => {
      let prev = Math.max(0, i - 1);
      while (prev > 0 && isStepConsumed(prev)) prev -= 1;
      return prev;
    });
  };

  // Dimming is four plain rectangles surrounding the highlighted target (or
  // one rectangle covering the whole viewport when nothing's highlighted),
  // and the ring is a plain border with SHARP corners -- deliberately the
  // same sharp rectangle on BOTH, with zero border-radius and zero masking
  // anywhere. This used to vary (a circular hole+ring for small icon
  // targets, via a CSS mask-image SVG data URI) -- reverted after live
  // verification showed the mask's own math was pixel-perfect (ring and
  // hole centers matched exactly, confirmed via computed-style inspection)
  // yet the user still reported a real, visible misalignment on their own
  // screenshots. Since this project has already been burned once by a
  // different exotic-CSS-on-position:fixed technique (box-shadow spread)
  // rendering correctly in this environment's Chromium-based testing while
  // failing on the user's real device, and mask-image + position:fixed is
  // the same general risk category, the only way to make a mismatch
  // structurally impossible regardless of browser engine is to stop using
  // any technique more exotic than "plain rectangles with matching
  // coordinates" -- a sharp-cornered hole built from four ordinary
  // background-color strips can NEVER disagree with a sharp-cornered ring
  // built from the same rect, since both are driven by nothing but basic
  // position/size math with no separate rasterization step to fall out of
  // sync.
  const DIM_COLOR = "rgba(35, 37, 38, 0.82)";
  const dimRects = rect
    ? [
        // top strip
        { top: 0, left: 0, width: window.innerWidth, height: Math.max(0, rect.top - 8) },
        // bottom strip
        { top: rect.top + rect.height + 8, left: 0, width: window.innerWidth, height: Math.max(0, window.innerHeight - (rect.top + rect.height + 8)) },
        // left strip, same vertical band as the target
        { top: rect.top - 8, left: 0, width: Math.max(0, rect.left - 8), height: rect.height + 16 },
        // right strip
        { top: rect.top - 8, left: rect.left + rect.width + 8, width: Math.max(0, window.innerWidth - (rect.left + rect.width + 8)), height: rect.height + 16 }
      ]
    : [{ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }];

  // Purely decorative (border + a small, ordinary glow) -- a modest glow
  // blur radius is well within normal, reliable box-shadow territory,
  // unlike the earlier 9999px-spread dimming technique this never shared.
  const ringStyle = rect
    ? {
        top: rect.top - 8,
        left: rect.left - 8,
        width: rect.width + 16,
        height: rect.height + 16,
        border: "3px solid #00a274",
        boxShadow: "0 0 18px 4px rgba(0, 162, 116, 0.55)"
      }
    : null;

  let cardStyle;
  let tailDirection = null;
  let tailOffset = CARD_WIDTH / 2;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height) - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    const openBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    // The card's rendered width is CARD_WIDTH, unless the viewport is
    // narrower than that plus margins -- the tail offset needs clamping
    // against whichever is actually smaller, so it can never land past the
    // bubble's own rounded corner on a narrow phone.
    const effectiveWidth = Math.min(CARD_WIDTH, window.innerWidth - MARGIN * 2);
    // Normally the card's left edge lines up with the target's own left
    // edge (clamped to stay on-screen) -- reads fine for a single wide
    // target like a panel or a modal. For a TabBar button specifically
    // (step.centerCard), that left-aligns the card against whichever
    // narrow icon happens to be highlighted, leaving it visibly off-center
    // as the tour steps across the bar -- per direct user feedback, these
    // steps center the card on the viewport instead, with the tail still
    // pointing at the real (off-center) icon underneath it.
    const left = step.centerCard
      ? (window.innerWidth - effectiveWidth) / 2
      : Math.min(Math.max(rect.left, MARGIN), window.innerWidth - effectiveWidth - MARGIN);
    cardStyle = {
      position: "fixed",
      left: Math.max(MARGIN, left),
      width: CARD_WIDTH,
      maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
      ...(openBelow ? { top: rect.top + rect.height + 12 } : { bottom: window.innerHeight - rect.top + 12 })
    };
    // The card always stacks directly above or below its target (never
    // beside it), so the tail only ever needs to point straight up (card
    // below target) or straight down (card above target) -- its horizontal
    // position along that edge is lined up with the target's own
    // horizontal center, clamped to stay clear of the bubble's 14px
    // rounded corners.
    tailDirection = openBelow ? "up" : "down";
    const targetCenterX = rect.left + rect.width / 2;
    const TAIL_EDGE_MARGIN = 22;
    tailOffset = Math.min(Math.max(targetCenterX - cardStyle.left, TAIL_EDGE_MARGIN), effectiveWidth - TAIL_EDGE_MARGIN);
  } else {
    // Fully centered (both axes), per direct user request for every
    // borderless step. This used to be bottom-anchored instead, back when
    // the two "Discard" steps were ALSO borderless and opened a real Modal
    // as part of their onEnter action -- Modal.jsx's own backdrop always
    // top-aligns (align-items: flex-start), and a vertically-centered tour
    // card collided with that real modal's lower half. Those two steps now
    // highlight the real modal directly instead (`selector: '.comic-modal'`
    // -- see LIVE_DEMO_STEPS), so they go through the OTHER (rect-based)
    // branch above, not this one at all -- the only steps left here are
    // the intro, the outro, and "All Clear" (end of the live demo), none
    // of which ever have a real modal open behind them, so there's nothing
    // left for a centered card to collide with.
    cardStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: CARD_WIDTH,
      maxWidth: `calc(100vw - ${MARGIN * 2}px)`
    };
  }

  const tailStyles = tailDirection ? buildTailStyles(tailDirection, tailOffset) : null;

  // Shared between both card layouts below (speech-bubble-only for a
  // highlighted step, avatar+bubble for a borderless one) so they can't
  // drift out of sync with each other.
  const navRow = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
      <span data-tour="step-counter" style={{ fontSize: "12px", color: "#888", fontFamily: "'Anonymous Pro', monospace" }}>
        {stepIndex + 1} / {steps.length}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        {!isFirst && (
          <button
            type="button"
            onClick={handleBack}
            disabled={actionPending}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "#232526",
              fontFamily: "'Anonymous Pro', monospace",
              fontSize: "14px",
              textDecoration: "underline",
              cursor: actionPending ? "default" : "pointer",
              opacity: actionPending ? 0.5 : 1
            }}
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={handleNext}
          disabled={actionPending}
          className="comic-btn comic-btn--go"
          style={{ display: "inline-block", width: "auto", height: "36px", padding: "0 16px", margin: 0, fontSize: "14px", boxShadow: "3px 3px 0 0 #232526" }}
        >
          {isLast ? "Let's Go!" : "Next"}
        </button>
      </div>
    </div>
  );

  return createPortal(
    <>
      {/* Purely decorative -- pointerEvents: "none" on both this and the
          ring below makes them fully click-through, so any tap on a
          highlighted real control reaches that control, not this overlay.
          An onClick used to live here, intended as "tap the dimmed backdrop
          to skip" -- but when this was a single box-shadow-based div,
          box-shadow was never part of the element's actual hit area (only
          the small rect it was sized to was), so that handler only ever
          fired when tapping the HIGHLIGHTED element itself, not the dimmed
          area around it. Real bug that caused: tapping the glowing
          "Discard" button during the live-Mission demo silently ended the
          whole tour instead of reaching the real Discard button
          underneath. Skipping the tour is now only ever the explicit
          corner X button or Escape, both unambiguous. */}
      {dimRects.map((r, i) => (
        <div
          key={i}
          style={{
            position: "fixed",
            zIndex: 1100,
            pointerEvents: "none",
            background: DIM_COLOR,
            ...r
          }}
        />
      ))}
      {/* boxSizing: "border-box" is a real, previously-fixed bug worth
          keeping: without it, the default content-box model treats
          width/height as the CONTENT size only, with the 3px border then
          added OUTSIDE that on every side -- so a div styled width: 48/
          height: 48/border: 3px actually renders at 54x54, but since
          `top`/`left` still anchor its outer (border-box) corner at the
          position computed for the intended 48x48 box, the extra 6px only
          ever grows out to the bottom-right, never the top-left. That
          shifted the ring's visual center down-and-right by half the
          overflow (3px) -- confirmed precisely via measurement at the
          time. border-box makes the specified width/height include the
          border, so the ring's rendered size matches what was actually
          intended and stays genuinely concentric with the real target. */}
      {ringStyle && (
        <div
          style={{
            position: "fixed",
            boxSizing: "border-box",
            zIndex: 1100,
            pointerEvents: "none",
            ...ringStyle
          }}
        />
      )}
      <div style={{ ...cardStyle, zIndex: 1101 }} onClick={(e) => e.stopPropagation()}>
        {rect ? (
          // Something specific is highlighted: the card itself IS the
          // speech bubble (no profile picture -- per direct user request,
          // the avatar is reserved for the no-target case below), with a
          // tail pointing at whatever's highlighted rather than at a fixed
          // avatar.
          <div className="comic-tour-bubble">
            <div style={{ position: "absolute", top: "-10px", right: "-10px" }}>
              <IconButton icon="close" onClick={handleFinish} color="#232526" background="#ffffff" label="Skip tour" size={28} />
            </div>
            <strong style={{ display: "block", marginBottom: "4px", fontFamily: "'Shadows Into Light Two', 'Shadows Into Light 2', cursive", fontSize: "18px" }}>{step.title}</strong>
            <div style={{ marginBottom: "10px" }}>{actionPending && !waitingForUser ? "One moment..." : step.text}</div>
            {navRow}
            <div style={tailStyles.outer} />
            <div style={tailStyles.inner} />
          </div>
        ) : (
          // No specific target for this step (the intro/outro cards) --
          // the original look: the Hero's own avatar beside a speech
          // bubble, its tail pointing at the avatar (comic.css's existing
          // fixed left-pointing .comic-speech-bubble tail).
          <div className="comic-panel" style={{ padding: "16px", position: "relative", background: "#ffffff" }}>
            <div style={{ position: "absolute", top: "-10px", right: "-10px" }}>
              <IconButton icon="close" onClick={handleFinish} color="#232526" background="#ffffff" label="Skip tour" size={28} />
            </div>
            <div className="comic-speech-row" style={{ margin: "0 0 10px 0" }}>
              <InitialAvatar name={activeProfile?.name} photoURL={activeProfile?.photoURL} size={40} />
              <div className="comic-speech-bubble">
                <strong style={{ display: "block", marginBottom: "4px", fontFamily: "'Shadows Into Light Two', 'Shadows Into Light 2', cursive", fontSize: "18px" }}>{step.title}</strong>
                {actionPending && !waitingForUser ? "One moment..." : step.text}
              </div>
            </div>
            {navRow}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default GuidedTour;
